/**
 * Login - Google OAuth login page
 */
import { authLogger } from '@annote/core/utils/debug';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useCallback } from 'react';
import { useProfileContext } from 'src/context/ProfileContext';
import { useDataContext } from 'src/context/DataContext';
import { isSupabaseDatabase } from '@annote/core/dbs/supabaseDb';
import LoginTips from './LoginTips';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const AuthWrapper = styled.div`
  padding-top: 10rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SplitWrapper = styled.div`
  display: flex;
  min-height: 100vh;
`;

const LeftPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  background: #fff;
`;

const RightPane = styled.div`
  flex: 1;
  background: radial-gradient(ellipse 120% 120% at 50% 30%, rgba(255, 212, 0, 0.09) 0%, #fffdf7 70%);
  border-left: 1px solid #f0ece0;

  @media (max-width: 900px) {
    display: none;
  }
`;

/* The full wordmark: angular A + "nnote" with the yellow highlight box
   splitting the first n (same geometry as the marketing site header) */
const Brand = styled.div`
  position: relative;
  display: inline-block;
  white-space: nowrap;
  line-height: 1;
  margin-bottom: 2.5rem;

  svg {
    position: relative;
    z-index: 1;
    width: 17.4px;
    height: 21px;
    margin-right: 1px;
    vertical-align: baseline;
  }

  span {
    position: relative;
    z-index: 1;
    font-family: 'DM Sans', system-ui, sans-serif;
    font-weight: 500;
    font-size: 22px;
    color: #000;
    letter-spacing: -0.01em;
  }
`;

const BrandHl = styled.i`
  position: absolute;
  left: -6px;
  top: 3.6px;
  width: 35px;
  height: 22px;
  background: #f2d127;
  z-index: 0;
`;

const WelcomeTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-family: 'Baskervville', Georgia, serif;
  font-weight: 400;
  font-size: 2rem;
  color: #373530;
`;

const WelcomeSub = styled.p`
  margin: 0 0 2rem;
  max-width: 320px;
  text-align: center;
  color: #5f5d59;
  line-height: 1.6;
`;

const NewHereRow = styled.p`
  margin-top: 2.5rem;
  font-size: 0.9375rem;
  color: #878683;

  a {
    color: #5d6dd9;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const DevLoginSection = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const DevLoginText = styled.p`
  margin-bottom: 1rem;
  color: #666;
`;

const DevLoginButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }

  &:active {
    transform: translateY(1px);
  }
`;

interface CredentialResponse {
  credential: string;
}

const LoginError = styled.p`
  margin-top: 1.5rem;
  max-width: 420px;
  text-align: center;
  color: #c0392b;
`;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { annoteDB, isInitialized } = useDataContext();

  const navigateAfterLogin = useCallback((): void => {
    const redirectTo = localStorage.getItem('redirectTo');
    localStorage.removeItem('redirectTo');
    // Default to /flow (an authenticated route) instead of '/' (ComingSoon)
    navigate(redirectTo || '/flow');
  }, [navigate]);

  // Callback for when the user signs in with Google
  const handleCredentialResponse = useCallback(
    async (response: CredentialResponse): Promise<void> => {
      setLoginError(null);
      if (!annoteDB) {
        authLogger.error('handleCredentialResponse: AnnoteDB not initialized');
        setLoginError('Something went wrong initializing Annote. Please reload the page and try again.');
        return;
      }

      const { serverDB } = annoteDB;
      if (!serverDB) {
        // annoteDB is appMessageDb - the extension owns the session, so forward
        // the Google credential to it instead of dropping the sign-in.
        try {
          if (!annoteDB.signInWithGoogleCredential) {
            throw new Error('Extension database cannot accept a Google credential');
          }
          const extensionProfile = await annoteDB.signInWithGoogleCredential(response.credential);
          if (!extensionProfile) {
            // Signed in, but the profile row isn't available yet (e.g. fresh
            // install) - proceed and let the app fetch it.
            authLogger.warn('Extension sign-in succeeded but no profile was returned yet');
          }
          navigateAfterLogin();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          authLogger.error('Extension sign-in error:', message);
          setLoginError(
            'Signing in through the Annote extension failed. ' +
              'Try clicking the Annote icon in your browser toolbar to sign in, ' +
              'or temporarily disable the extension and sign in here. ' +
              `(${message})`,
          );
        }
        return;
      }

      const supabaseClient = serverDB.db;
      if (!supabaseClient) {
        authLogger.error('handleCredentialResponse: supabaseClient not initialized');
        setLoginError('Something went wrong connecting to Annote. Please reload the page and try again.');
        return;
      }

      try {
        const { data, error } = await supabaseClient.auth.signInWithIdToken({
          provider: 'google',
          token: response.credential,
        });

        if (error) {
          // Provide helpful error message for common configuration issues
          let errorMessage = error.message;
          if (error.message.includes('not enabled')) {
            errorMessage =
              'Google OAuth provider is not enabled in Supabase. ' +
              'Please enable it in your Supabase project settings: Authentication > Providers > Google. ' +
              `Error: ${error.message}`;
          }
          throw new Error(`Supabase sign-in failed: ${errorMessage}`);
        }

        if (!data?.user) {
          throw new Error('No user data received after successful sign-in');
        }

        await serverDB.init();

        // Profile is now available in annoteDB.profile after init
        if (!annoteDB.profile) {
          throw new Error('Failed to fetch user profile');
        }

        // Redirect after successful login
        navigateAfterLogin();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        authLogger.error('Authentication error:', message);
        setLoginError(`Sign-in failed: ${message}`);
      }
    },
    [annoteDB, navigateAfterLogin],
  );

  // Non-blocking auth check - show login form immediately, check auth in background
  // This handles the rare case where user is already authenticated (e.g., from another tab)
  useEffect(() => {
    // Only check if we have a profile (rare case - user already logged in)
    // If no profile, skip the check entirely and show login form immediately
    if (!profile || !isInitialized || !annoteDB) {
      return;
    }

    const checkAuthStatus = async (): Promise<void> => {
      try {
        // Check if this is a Supabase database and verify authentication
        if (isSupabaseDatabase(annoteDB)) {
          const isAuthenticated = await annoteDB.isAuthenticated();
          if (isAuthenticated) {
            // User is actually authenticated, redirect to authenticated route
            authLogger.debug('[Login] User already authenticated, redirecting from login');
            const redirectTo = localStorage.getItem('redirectTo');
            localStorage.removeItem('redirectTo');
            navigate(redirectTo || '/flow');
          } else {
            // Profile exists but session is invalid - clear the stale profile
            authLogger.warn('[Login] Stale profile detected, clearing it');
            if (annoteDB.serverDB && isSupabaseDatabase(annoteDB.serverDB)) {
              await annoteDB.serverDB.db.auth.signOut();
            }
            // Profile will be cleared by auth state change listener
          }
        } else {
          // Extension database - if profile exists, user is authenticated
          authLogger.debug('[Login] User authenticated via extension, redirecting from login');
          navigate('/flow');
        }
      } catch (error) {
        authLogger.error(
          '[Login] Error checking auth status:',
          error instanceof Error ? error.message : 'Unknown error',
        );
        // On error, just show login form (non-blocking)
      }
    };

    // Run check in background (non-blocking)
    checkAuthStatus();
  }, [profile, annoteDB, isInitialized, navigate]);

  useEffect(() => {
    // Add global error handler for iframe cross-origin errors
    const handleError = (event: ErrorEvent): void => {
      // Check if this is the Google Sign-In iframe error
      if (
        event.message &&
        typeof event.message === 'string' &&
        (event.message.includes('Blocked a frame') ||
          event.message.includes('accounts.google.com') ||
          event.message.includes('cross-origin'))
      ) {
        authLogger.error('Google Sign-In iframe error (this is usually harmless):', event.message);
        // Don't prevent default - let it log but don't break the page
      }
    };

    window.addEventListener('error', handleError);

    const loadGoogleScript = (): void => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setIsGoogleScriptLoaded(true);
      };
      script.onerror = () => {
        authLogger.error('Failed to load Google script');
      };
      document.body.appendChild(script);
    };

    if (!window.google) {
      loadGoogleScript();
    } else {
      setIsGoogleScriptLoaded(true);
    }

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    // Not yet logged in - Setup Google Sign-In
    // Show login form once DB is initialized and Google script is loaded
    if (!profile && annoteDB && isGoogleScriptLoaded && window.google && window.google.accounts) {
      if (!GOOGLE_CLIENT_ID) {
        authLogger.error('VITE_GOOGLE_CLIENT_ID is not set - Google Sign-In will not work');
        return;
      }
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          // One Tap enabled - iframe errors are caught and logged but shouldn't block login
          cancel_on_tap_outside: true,
        });

        // Render the Google Sign-In button
        const button = document.getElementById('google-signin-button');
        if (button) {
          window.google.accounts.id.renderButton(button, {
            theme: 'outline',
            size: 'large',
          });
        } else {
          authLogger.error('Google Sign-In button element not found');
        }
      } catch (error) {
        authLogger.error(
          'Error initializing Google Sign-In:',
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    }
  }, [profile, annoteDB, isGoogleScriptLoaded, handleCredentialResponse]);

  // Show loading state only while database is initializing
  if (!isInitialized) {
    return (
      <AuthWrapper>
        <div>Loading...</div>
      </AuthWrapper>
    );
  }

  return (
    <SplitWrapper>
      <LeftPane>
        <Brand aria-label="Annote">
          <BrandHl aria-hidden="true" />
          <svg viewBox="8 0 24 29" aria-hidden="true">
            <path
              d="M32 29H27.96L25.0684 23.9229C25.0264 23.8491 24.9481 23.8028 24.8633 23.8027H15.1367C15.0526 23.8028 14.9749 23.8481 14.9326 23.9209L12 29H8V10L32 0V29ZM17 13.4189V18H23V11L17 13.4189Z"
              fill="black"
            />
          </svg>
          <span>nnote</span>
        </Brand>
        <WelcomeTitle>Welcome back</WelcomeTitle>
        <WelcomeSub>Sign in with Google to sync your highlights, notes, and links everywhere.</WelcomeSub>

        <div id="google-signin-button"></div>

        {loginError && <LoginError>{loginError}</LoginError>}

        <NewHereRow>
          New to Annote?{' '}
          <a href="https://chromewebstore.google.com/detail/annote/becaeodideblenfemdnhjgbipehcphia">
            Get the extension
          </a>{' '}
          to start annotating.
        </NewHereRow>

        {process.env.NODE_ENV === 'development' && (
          <DevLoginSection>
            <DevLoginText>For development, you can login as the seed user.</DevLoginText>
            <DevLoginButton onClick={() => navigate('/login/dev')}>🔧 Dev Login (Offline)</DevLoginButton>
          </DevLoginSection>
        )}
      </LeftPane>
      <RightPane>
        <LoginTips />
      </RightPane>
    </SplitWrapper>
  );
};

export default Login;
